import React from "react";
import Slider from "react-slick";
import Button from "@mui/material/Button";
import CssBaseline from "@mui/material/CssBaseline";
import Container from "@mui/material/Container";
import { Box, Grid, Link, Typography } from "@mui/material";
import { makeStyles } from "@mui/styles";

import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";

import farmAppLogo from "../assets/landing/logo/farm-app-logo.png";
import youtube from "../assets/landing/logo/youtube.png";
import satelliteDishIcon from "../assets/landing/icons/satellite-dish-icon.png";
import cropDoctorIcon from "../assets/landing/icons/crop-doctor-icon.png";
import soilTestingIcon from "../assets/landing/icons/soil-testing-icon.png";
import marketPlaceIcon from "../assets/landing/icons/market-place-icon.png";
import landingPageBackground from "../assets/landing/images/landing-page-background.png";
import { APP_URL } from "../globals/const";
import { useTranslation } from "react-i18next";

const useStyles = makeStyles((theme) => ({
  root: {
    maxHeight: "100vh",
    margin: "auto",
  },
  featureText: {
    fontSize: "12px !important",
    fontWeight: "500 !important",
    textAlign: "center",
    marginBottom: "10px !important",
  },
  title: {
    fontSize: "18px !important",
    fontWeight: "bold !important",
    margin: "12px 0px 10px 0px !important",
  },
  subTitle: {
    fontSize: "14px !important",
    lineHeight: "20px !important",
    color: "#444444",
  },
  // Custom carousel indicator styles
  customIndicatorStyles: {
    "& .slick-dots": {
      bottom: "-10px",
    },
    "& .slick-dots li": {
      margin: "0 4px",
    },
    "& .slick-dots li button:before": {
      fontSize: "10px",
      color: "#bbbbbb",
      opacity: 0.6,
    },
    "& .slick-dots li.slick-active button:before": {
      color: "#1976d2",
      opacity: 0.9,
    },
  },
}));

const LandingMobile = () => {
  const classes = useStyles();
  const { t } = useTranslation();

  const features = [
    { icon: satelliteDishIcon, label: t("Satellite Data"), color: "#E9F5FF" },
    { icon: cropDoctorIcon, label: t("Crop Doctor"), color: "#E9D8F4CC" },
    { icon: soilTestingIcon, label: t("Soil Testing"), color: "#95D6A433" },
    { icon: marketPlaceIcon, label: t("Marketplace"), color: "#FFBC8533" },
  ];

  return (
    <Container
      component="main"
      maxWidth="xs"
      sx={{
        overflow: "hidden",
        position: "relative",
        px: 0,
        "&::before": {
          content: '""',
          position: "absolute",
          top: "10px",
          left: "0px",
          width: "100%",
          height: "100%",
          backgroundImage: `url(${landingPageBackground})`,
          backgroundSize: "cover",
          opacity: 0.12,
          zIndex: -1,
          borderRadius: "12px",
        },
      }}
    >
      <CssBaseline />

      {/* Improved header section with logo and buttons in one area */}
      <Box
        sx={{
          pt: 3,
          pb: 2,
          px: 3,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          borderBottom: "1px solid rgba(0,0,0,0.07)",
          background:
            "linear-gradient(180deg, rgba(255,255,255,0.9) 0%, rgba(255,255,255,0.65) 100%)",
          position: "relative",
          zIndex: 1,
        }}
      >
        {/* Logo and title section */}
        <img
          src={farmAppLogo}
          style={{
            width: "96px",
            height: "96px",
            objectFit: "contain",
          }}
        />
        <Typography
          className={classes.title}
          sx={{
            fontSize: "16px !important",
            mt: 1,
            mb: 0.5,
            background: "linear-gradient(90deg, #1976d2 0%, #4caf50 100%)",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
          }}
        >
          {t("Traditional Agriculture to AI Agriculture")}
        </Typography>
        <Typography
          className={classes.subTitle}
          sx={{
            mb: 3,
            maxWidth: "85%",
            textAlign: "center",
          }}
        >
          {t("Increase Farm Yield and Reduce Costs Through AI Agriculture")}
        </Typography>

        {/* Login/Signup buttons row */}
        <Box
          sx={{
            display: "flex",
            gap: "16px",
            width: "100%",
            justifyContent: "center",
            maxWidth: "350px",
          }}
        >
          <Box sx={{ width: "100%" }}>
            <Link href={`${APP_URL}/login`} style={{ textDecoration: "none" }}>
              <Button
                variant="outlined"
                sx={{
                  width: "100%",
                  py: 1,
                  fontFamily: "Poppins !important",
                  fontWeight: "600",
                  borderRadius: "8px",
                  borderWidth: "1.5px",
                  "&:hover": {
                    borderWidth: "1.5px",
                  },
                }}
              >
                {t("Login")}
              </Button>
            </Link>
          </Box>
          <Box sx={{ width: "100%" }}>
            <Link
              href={`${APP_URL}/enquiry`}
              style={{ textDecoration: "none" }}
            >
              <Button
                variant="contained"
                color="primary"
                sx={{
                  width: "100%",
                  py: 1,
                  color: "white",
                  fontWeight: "600",
                  borderRadius: "8px",
                  boxShadow: "0 4px 10px rgba(0, 0, 0, 0.15)",
                  transition: "transform 0.2s",
                  "&:hover": {
                    transform: "translateY(-2px)",
                    boxShadow: "0 6px 15px rgba(0, 0, 0, 0.2)",
                  },
                }}
              >
                {t("Enquiry")}
              </Button>
            </Link>
          </Box>
        </Box>
      </Box>

      <Box
        sx={{
          mt: 3,
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          px: "35px",
        }}
      >
        {/* Rest of your content (feature grid, etc.) stays the same */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {features.map((feature, index) => (
            <Grid item xs={6} key={index}>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  transition: "transform 0.3s",
                  "&:hover": {
                    transform: "translateY(-4px)",
                  },
                }}
              >
                <Box
                  sx={{
                    height: "95px",
                    width: "100%",
                    backgroundColor: feature.color,
                    borderRadius: "12px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    marginBottom: "10px",
                    boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                  }}
                >
                  <img
                    src={feature.icon}
                    alt={feature.label}
                    style={{
                      width: "60px",
                      height: "54px",
                      objectFit: "contain",
                    }}
                  />
                </Box>
                <Typography className={classes.featureText}>
                  {feature.label}
                </Typography>
              </Box>
            </Grid>
          ))}
        </Grid>

        <Box
          sx={{
            position: "relative",
            width: "100%",
            cursor: "pointer",
            borderRadius: "12px",
            overflow: "hidden",
            boxShadow: "0 10px 20px rgba(0,0,0,0.15)",
            mb: 3,
            "&:hover img:first-of-type": {
              transform: "scale(1.03)",
            },
          }}
          onClick={() =>
            window.open("https://www.youtube.com/watch?v=M2sI8jYV_fU", "_blank")
          }
        >
          {/* Thumbnail Image */}
          <img
            src="https://img.youtube.com/vi/M2sI8jYV_fU/maxresdefault.jpg"
            alt="YouTube Thumbnail"
            style={{
              width: "100%",
              transition: "transform 0.4s",
            }}
          />

          {/* YouTube Play Button with better styling */}
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              backgroundColor: "rgba(255, 0, 0, 0.7)",
              borderRadius: "50%",
              width: "50px",
              height: "50px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              transition: "all 0.3s",
              "&:hover": {
                backgroundColor: "rgba(255, 0, 0, 0.9)",
                width: "55px",
                height: "55px",
              },
            }}
          >
            <img
              src={youtube}
              alt="YouTube Play Button"
              style={{
                width: "22px",
                height: "22px",
              }}
            />
          </Box>
        </Box>

        <Box
          sx={{
            px: "10px",
            display: "block",
            width: "100%",
            backgroundColor: "#f8f8f8",
            borderRadius: "10px",
            padding: "16px",
            mb: 2,
          }}
          className={classes.customIndicatorStyles}
        >
          <Slider
            dots={true}
            infinite={true}
            speed={500}
            slidesToShow={1}
            slidesToScroll={1}
            adaptiveHeight={true}
            arrows={false}
            autoplay={true}
            autoplaySpeed={4000}
            cssEase="ease-out"
            pauseOnHover={true}
          >
            <Box>
              <Typography
                className={classes.title}
                sx={{ fontWeight: "bold", color: "#2e7d32" }}
              >
                {t("Biochar Campaign")}
              </Typography>
              <Typography className={classes.subTitle}>
                {t(
                  "Join Us In Our Mission To Make A Greener And Cleaner India For All Through Active Residue Management"
                )}
                !
              </Typography>
              <Button
                variant="outlined"
                color="success"
                component="a"
                href={`${APP_URL}/biochar-campaign`}
                size="small"
                sx={{
                  mt: 1.5,
                  borderRadius: "20px",
                  textTransform: "none",
                  fontWeight: "600",
                  mb: 2,
                  "&:hover": { backgroundColor: "#e8f5e9" },
                }}
              >
                {t("Express interest in biochar")}
              </Button>
            </Box>
            <Box>
              <Typography className={classes.title} sx={{ color: "#1565c0" }}>
                {t(
                  "Are you interested in selling commodities on Farmapp as FPO"
                )}
                ?
              </Typography>
              <Button
                variant="outlined"
                color="primary"
                component="a"
                href={`${APP_URL}/fpo-interest-form`}
                size="small"
                sx={{
                  mt: 1.5,
                  borderRadius: "20px",
                  textTransform: "none",
                  fontWeight: "600",
                  mb: 2,
                  "&:hover": { backgroundColor: "#e3f2fd" },
                }}
              >
                {t("Fill Form")}
              </Button>
            </Box>
          </Slider>
        </Box>

        <Typography
          variant="body2"
          align="center"
          sx={{
            fontSize: "12px !important",
            my: "20px",
            color: "#555555",
            "& a": {
              color: "#1976d2",
              textDecoration: "none",
              "&:hover": {
                textDecoration: "underline",
              },
            },
          }}
        >
          {"Copyright © "}
          <Link href="https://asqi.in/">ASQI</Link> {new Date().getFullYear()}
        </Typography>
      </Box>
    </Container>
  );
};

export default LandingMobile;
